// src/components/YandexMetrica.tsx
import { useRouter } from 'next/router';
import Script from 'next/script';

// SCRUM-1508: Yandex Metrica is the analytics provider used by the Russian audience. Loading it on
// every locale cost a third-party connection for the ~90% of visitors who never see the Russian
// site, and tag.js resolves its event-recorder channel by region -- outside Russia it opens a
// WebSocket to wss://mc.yandex.com/solid.ws, a host that does not resolve on every network, and the
// failed handshake logs the console error that fails the Lighthouse Best Practices audit. Rendering
// only under `ru` keeps the counter where the team reads it and takes the tag off every other page.
//
// SCRUM-1954: the cookie notice is informational, so there is no consent gate — the counter renders on
// arrival under `ru`, and so does the `<noscript>` pixel (it left with the gate by mistake, and the product
// decision is not to lose tracking).
const RU_LOCALE = 'ru';

// localeDetection is off in next-i18next.config.js, so also inspect asPath to stay correct on hard
// navigations to /ru even if Next hasn't resolved the locale from the prefix.
function isRussianLocale(locale: string | undefined, asPath: string | undefined): boolean {
  if (locale === RU_LOCALE) return true;
  return Boolean(asPath?.startsWith(`/${RU_LOCALE}/`) || asPath === `/${RU_LOCALE}`);
}

export default function YandexMetrica() {
  const router = useRouter();
  if (!isRussianLocale(router.locale, router.asPath)) return null;

  return (
    <>
      {/* lazyOnload: analytics-only tag — it used to be a raw <script> inside <Head>, executing at HTML
          parse time on every page and competing with hydration; deferring it to browser idle changes no
          tracking semantics (the ym queue buffers events fired before the tag loads). */}
      <Script
        id='yandex-metrica'
        strategy='lazyOnload'
        dangerouslySetInnerHTML={{
          __html: `
                          (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){
                          (m[i].a=m[i].a||[]).push(arguments)};
                          m[i].l=1*new Date();
                          for (var j = 0; j < document.scripts.length; j++) {
                            if (document.scripts[j].src === r) { return; }
                          }
                          k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,
                          a.parentNode.insertBefore(k,a)})
                          (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

                          ym(101299376, "init", {
                            clickmap:true,
                            trackLinks:true,
                            accurateTrackBounce:true,
                            ecommerce:"dataLayer"
                          });
                        `,
        }}
      />

      <noscript>
        <div>
          <img loading='lazy' src='https://mc.yandex.ru/watch/101299376' style={{ position: 'absolute', insetInlineStart: '-9999px' }} alt='' />
        </div>
      </noscript>
    </>
  );
}
